import React, { useEffect, useState } from 'react';

import { BiX } from 'react-icons/bi';
import { GoDash, GoPlus } from 'react-icons/go';

import { Radio, RadioGroup } from '@material-ui/core';
import { calcOrderPrice } from '../../../../utils/calcOrderPrice';
import { formateBrPrice } from '../../../../utils/formateBrPrice';

import Button from '../../../../newComponents/Button';
import Complement from '../Complement';

import * as SM from '../ModalProduct/styles';
import * as S from './styles';

import api from '../../../../services/api';
import { useAuth } from '../../../../hooks/auth';
import { useToast } from '../../../../hooks/toast';
import PizzaComponent from '../PizzaOption';
import TextArea from '../../../../newComponents/TextArea';
import { IProduct } from '../../../../interfaces/IProduct';
import { ISelectedComplement } from '../ModalProduct';
import { IColor } from '../../../../interfaces/IColor';
import { ISize } from '../../../../interfaces/ISize';
import { IGrid } from '../../../../interfaces/IGrid';
import { ICartItem } from '../../../../interfaces/ICartItem';

interface PizzaProductProps {
  onClose: () => void;
  product: IProduct;
  itemToCart: (payload: Omit<ICartItem, 'id'>) => void;
  colorsAndSizesAll: {
    colors: IColor[] | null;
    sizes: ISize[] | null;
  };
  cartItem?: ICartItem;
}

const PizzaProduct: React.FC<PizzaProductProps> = (props) => {
  const { onClose, product, itemToCart, colorsAndSizesAll, cartItem } = props;

  const { user } = useAuth();
  const { addToast } = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [quantity, setQuantity] = useState(cartItem?.quantity ?? 1);
  const [priceWithOffer, setPriceWithOffer] = useState(0);
  const [note, setNote] = useState(cartItem?.observation || '');
  const [selectedComplements, setSelectedComplements] = useState<
    ISelectedComplement[]
  >([]);
  const [groupsUnderMinimum, setGroupsUnderMinimum] = useState<
    { id: number; under: boolean }[]
  >([]);
  const [anyGroupUnder, setAnyGroupUnder] = useState(false);

  const [selectedSize, setSelectedSize] = useState<IGrid | null>(null);
  const [step, setStep] = useState(1);

  const [pizzaOptions, setPizzaOptions] = useState<any[]>([]);
  const [selectedPizzaOptions, setSelectedPizzaOptions] = useState<
    { produto: IProduct; quantidade: number }[]
  >([]);
  const [selectedPizzaOptionsTemp, setSelectedPizzaOptionsTemp] = useState<
    { produto: IProduct; quantidade: number }[]
  >([]);
  const [numSelectedFlavors, setNumSelectedFlavors] = useState(0);
  const [maxFlavorsSelected, setMaxFlavorsSelected] = useState(false);

  const handleSelectedComplement = (
    cartComponent: ISelectedComplement,
  ): void => {
    const grupo = product.complements.find(
      (group) => cartComponent.complement.complementId === group.id,
    );

    if (!grupo) return;

    setSelectedComplements((old) => {
      let temp: ISelectedComplement[] = [];

      if (+grupo.maximumQuantity === 1) {
        temp = selectedComplements.filter(
          (item) =>
            item.complement.id !== cartComponent.complement.id &&
            item.complement.complementId !==
              cartComponent.complement.complementId,
        );
      } else {
        temp = selectedComplements.filter(
          (item) => item.complement.id !== cartComponent.complement.id,
        );
      }

      if (cartComponent.quantity > 0) temp.push(cartComponent);

      return temp;
    });
  };

  const handleAddItemToCart = (): void => {
    itemToCart({
      compositeProduct: {
        product,
        complements: selectedComplements,
      },
      childOrderItems: selectedPizzaOptionsTemp,
      size:
        colorsAndSizesAll.sizes?.find(
          (size) => size.id === selectedSize?.sizeId,
        )?.name || '',
      quantity,
      observation: note,
    });

    onClose();
  };

  const handleRadioSelect = (value: string): void => {
    if (selectedSize && +value === selectedSize.id) {
      setSelectedSize(null);
    } else {
      const size = product.grids.find((_size) => +value === _size.id);

      if (size) setSelectedSize(size);
    }
  };

  const setPizzaComponent = (cartPizzaComponent: {
    produto: IProduct;
    quantidade: number;
  }): void => {
    setSelectedPizzaOptions((oldState) => {
      const temp = oldState.filter(
        (option) => cartPizzaComponent.produto.id !== option.produto.id,
      );

      if (cartPizzaComponent.quantidade > 0) temp.push(cartPizzaComponent);

      return temp;
    });
  };

  useEffect(() => {
    if (cartItem) {
      const size = product.grids.find((_size) => cartItem.size === _size.name);

      if (size) setSelectedSize(size);
    }
  }, [cartItem, product.grids]);

  useEffect(() => {
    if (selectedPizzaOptionsTemp.length > 0) {
      setPriceWithOffer(
        calcOrderPrice({
          id: product.id,
          compositeProduct: {
            product,
            complements: selectedComplements,
          },
          childOrderItems: selectedPizzaOptionsTemp,
          quantity,
          observation: note,
        }),
      );
    }
  }, [note, product, quantity, selectedComplements, selectedPizzaOptionsTemp]);

  useEffect(() => {
    product.complements.forEach((comp) => {
      if (groupsUnderMinimum !== undefined) {
        const tempGroupsUnder = groupsUnderMinimum;

        if (+comp.minimumQuantity > 0 && +comp.maximumQuantity > 1) {
          tempGroupsUnder.push({ id: comp.id, under: true });
        } else {
          tempGroupsUnder.push({ id: comp.id, under: false });
        }

        setGroupsUnderMinimum(tempGroupsUnder);
      } else if (+comp.minimumQuantity > 0 && +comp.maximumQuantity > 1) {
        setGroupsUnderMinimum([{ id: comp.id, under: true }]);
      } else {
        setGroupsUnderMinimum([{ id: comp.id, under: false }]);
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  useEffect(() => {
    let counter = 0;

    if (groupsUnderMinimum !== undefined) {
      groupsUnderMinimum.forEach((group) => {
        if (group.under) {
          setAnyGroupUnder(true);
        } else {
          counter += 1;
        }
      });
      if (counter === groupsUnderMinimum.length) {
        setAnyGroupUnder(false);
      }
    }
  }, [groupsUnderMinimum]);

  useEffect(() => {
    if (selectedSize) {
      setIsLoading(true);
      const fetchData = async (): Promise<void> => {
        try {
          const { data } = await api.get(
            `/companies/${user.empresa_id}/products/size/${selectedSize.sizeId}`,
          );
          setPizzaOptions(data);

          const temp = data.find((prod: any) => prod.id === product.id);
          if (temp !== undefined) {
            setSelectedPizzaOptions([{ produto: temp, quantidade: 1 }]);
          }
          setIsLoading(false);
        } catch (err) {
          addToast({
            type: 'error',
            title: 'Erro ao buscar outros tamanhos de pizza.',
          });
          setIsLoading(false);
        }
      };
      fetchData();
    }
  }, [addToast, product.id, selectedSize, user.empresa_id]);

  useEffect(() => {
    if (selectedSize && step === 2) {
      if (selectedSize.numberOfFlavors > 1) {
        addToast({
          type: 'info',
          title: 'Você pode adicionar sabores',
        });
      }
    }
  }, [addToast, selectedSize, step]);

  useEffect(() => {
    if (selectedPizzaOptions !== undefined) {
      if (selectedPizzaOptions.length > 0) {
        setNumSelectedFlavors(
          selectedPizzaOptions
            .map((option) => option.quantidade)
            .reduce((prev, next) => prev + next),
        );
      }
    }
  }, [selectedPizzaOptions]);

  useEffect(() => {
    if (selectedSize) {
      if (
        numSelectedFlavors ===
        colorsAndSizesAll.sizes?.find((size) => size.id === selectedSize.sizeId)
          ?.numOfFlavors
      ) {
        setMaxFlavorsSelected(true);
      } else {
        setMaxFlavorsSelected(false);
      }

      if (selectedPizzaOptions) {
        if (selectedPizzaOptions.length > 0) {
          setSelectedPizzaOptionsTemp(
            selectedPizzaOptions.map((option) => {
              return {
                ...option,
                quantidade: option.quantidade / numSelectedFlavors,
              };
            }),
          );
        }
      }
    }
  }, [
    colorsAndSizesAll.sizes,
    numSelectedFlavors,
    selectedPizzaOptions,
    selectedSize,
  ]);

  return (
    <div className="body">
      <div>
        <SM.ModalHeader>
          <h1>{product?.name}</h1>
          <button type="button" onClick={onClose}>
            <BiX />
          </button>
        </SM.ModalHeader>
        <SM.ModalBody>
          <h2>{product?.observation}</h2>

          {selectedSize?.stock &&
            selectedSize?.stock.active &&
            Number(selectedSize?.stock.currentStock) === 0 && (
              <p className="out__of__stock">Sem Estoque</p>
            )}

          {step === 1 ? (
            <RadioGroup value={String(selectedSize?.id) || ''}>
              {product.grids
                .sort((a: any, b: any) => a.price.value - b.price.value)
                .map((size: any, index: number) => (
                  <React.Fragment key={size.id}>
                    {index === 0 && <S.CustomDivider />}
                    <S.ComplementItem
                      onClick={() => handleRadioSelect(String(size.id))}
                      disabled={isLoading}
                    >
                      <div className="informations">
                        <p>{size.name}</p>
                        <p className="number_of_slices">
                          {`${size.numberOfSlices} pedaço(s)`}
                        </p>
                        <div className="price">
                          <p className="new">
                            {formateBrPrice(
                              Number(
                                size.discount?.active
                                  ? size.price.value * (1 - size.discount.value)
                                  : size.price.value,
                              ),
                            )}
                          </p>
                          {size.discount?.active && (
                            <p className="old">
                              {formateBrPrice(Number(size.price.value))}
                            </p>
                          )}
                        </div>
                      </div>
                      <Radio value={String(size.id)} disabled={isLoading} />
                    </S.ComplementItem>
                    <S.CustomDivider />
                  </React.Fragment>
                ))}
            </RadioGroup>
          ) : (
            <>
              <h3>
                Tamanho escolhido: <span>{selectedSize?.name}</span>
              </h3>
              <S.CustomDivider />

              <p>
                {`Você pode selecionar até ${
                  colorsAndSizesAll.sizes?.find(
                    (size) => size.id === selectedSize?.sizeId,
                  )?.numOfFlavors
                } sabores`}
              </p>

              <p>Sabores selecionados: {numSelectedFlavors}</p>

              <S.CustomDivider />

              {selectedSize &&
                (colorsAndSizesAll.sizes?.find(
                  (size) => size.id === selectedSize.sizeId,
                )?.numOfFlavors || 0) > 1 &&
                pizzaOptions.map((option: any, index: number) => (
                  <>
                    {index !== 0 && <S.CustomDivider />}
                    <PizzaComponent
                      key={option.id}
                      pizza={option}
                      setPizzaComponent={setPizzaComponent}
                      base={option.grids[0].id === selectedSize?.id}
                      full={maxFlavorsSelected}
                      numSelectedFlavors={numSelectedFlavors}
                      cartItem={cartItem?.childOrderItems.find(
                        (item) =>
                          item.produto.id === option.id &&
                          item.produto.grids[0].sizeId ===
                            option.grids[0].sizeId,
                      )}
                    />
                  </>
                ))}

              <S.CustomDivider />

              {product.complements
                .sort((a, b) => a.order - b.order)
                .map((complement) => (
                  <Complement
                    key={complement.id}
                    cartComplement={cartItem?.compositeProduct.complements.find(
                      (comp) => comp.complement.complementId === complement.id,
                    )}
                    complement={complement}
                    handleSelectedComplement={handleSelectedComplement}
                    selectedComplements={selectedComplements}
                    groupsUnderMinimum={groupsUnderMinimum}
                    setGroupsUnderMinimum={setGroupsUnderMinimum}
                  />
                ))}

              <div className="quantity">
                <SM.CustomIconButton
                  size="small"
                  disabled={quantity === 1}
                  onClick={() => setQuantity(quantity - 1)}
                >
                  <GoDash />
                </SM.CustomIconButton>
                <p>{quantity}</p>
                <SM.CustomIconButton
                  size="small"
                  disabled={
                    selectedSize?.stock &&
                    selectedSize.stock.active &&
                    selectedPizzaOptionsTemp.length > 0 &&
                    selectedPizzaOptionsTemp
                      .map(
                        (_product: any) =>
                          _product.quantidade * (quantity + 1) >
                          _product.produto.grids[0].stock.currentStock,
                      )
                      .reduce((prev, next) => prev || next)
                  }
                  onClick={() => setQuantity(quantity + 1)}
                >
                  <GoPlus />
                </SM.CustomIconButton>
              </div>
              <div className="comments">
                <TextArea
                  placeholder="Observação"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>
            </>
          )}
        </SM.ModalBody>
        <SM.ModalFooter>
          {step === 1 ? (
            <>
              <Button
                full
                onClick={() => setStep(2)}
                disabled={isLoading || !selectedSize}
              >
                {isLoading ? 'Carregando Sabores...' : 'Avançar'}
              </Button>
              <Button full variant="outlined" onClick={onClose}>
                Cancelar
              </Button>
            </>
          ) : (
            <>
              <Button
                full
                disabled={
                  anyGroupUnder ||
                  (selectedSize?.stock &&
                    selectedSize.stock.active &&
                    +selectedSize.stock.currentStock < quantity)
                }
                onClick={handleAddItemToCart}
              >
                {`Adicionar ${formateBrPrice(+priceWithOffer)}`}
              </Button>
              <Button full variant="outlined" onClick={() => setStep(1)}>
                Voltar
              </Button>
            </>
          )}
        </SM.ModalFooter>
      </div>
    </div>
  );
};

export default PizzaProduct;
